import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/apiClient";
import { toast } from "sonner";
import { SeverityBadge } from "@/components/SeverityBadge";
import { SmsStatusPanel } from "@/components/SmsStatusPanel";
import { Link } from "react-router-dom";
import { Shield, AlertOctagon, UserCheck, Clock, ScrollText, Settings } from "lucide-react";

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [pending, setPending] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [audit, setAudit] = useState([]);
  const [auditFilter, setAuditFilter] = useState("");

  const refresh = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/pending-experts").then((r) => setPending(r.data)).catch(() => {});
    api.get("/admin/escalations").then((r) => setEscalations(r.data)).catch(() => {});
    api.get("/admin/audit-logs?limit=100").then((r) => setAudit(r.data)).catch(() => {});
  };

  useEffect(() => {
    refresh();
  }, []);

  const act = async (eid, action) => {
    try {
      await api.post(`/admin/experts/${eid}/${action}`);
      toast.success(`Expert ${action}d`);
      refresh();
    } catch (e) {
      toast.error(extractError(e));
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <span className="uppercase-label inline-flex items-center gap-1"><Shield size={14} /> Platform Admin</span>
          <h1 className="font-display text-3xl mt-2">Overview</h1>
        </div>
        <Link
          to="/admin/settings"
          data-testid="admin-settings-link"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[color:var(--mano-border)] hover:border-[color:var(--mano-primary)] hover:text-[color:var(--mano-primary)] text-sm font-semibold transition-colors"
        >
          <Settings size={14} /> Settings
        </Link>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { l: "Users", v: stats.users },
            { l: "Experts", v: stats.experts },
            { l: "Approved", v: stats.approved_experts },
            { l: "Pending", v: stats.pending_experts },
            { l: "Reports", v: stats.reports },
            { l: "Bookings", v: stats.bookings },
            { l: "Open escalations", v: stats.escalations_open },
          ].map((s) => (
            <div key={s.l} className="rounded-2xl bg-white border border-[color:var(--mano-border)] p-4">
              <div className="uppercase-label">{s.l}</div>
              <div className="font-display text-2xl mt-1">{s.v}</div>
            </div>
          ))}
        </div>
      )}

      <SmsStatusPanel />

      <section>
        <div className="uppercase-label inline-flex items-center gap-1"><UserCheck size={14} /> Pending Service Providers</div>
        <div className="mt-3 space-y-2">
          {pending.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-[color:var(--mano-ink-soft)]">
              No pending applications.
            </div>
          )}
          {pending.map((p) => (
            <div key={p.id} className="rounded-2xl border border-[color:var(--mano-border)] bg-white p-5 flex items-center gap-4 flex-wrap">
              <img src={p.avatar} alt="" className="h-14 w-14 rounded-xl object-cover" />
              <div className="flex-1 min-w-[200px]">
                <div className="font-display text-lg">{p.name}</div>
                <div className="text-sm text-[color:var(--mano-ink-soft)]">{p.credentials}</div>
                <div className="text-xs uppercase-label mt-1">{p.specialization}</div>
              </div>
              <div className="flex gap-2">
                <button data-testid={`admin-approve-${p.id}`} onClick={() => act(p.id, "approve")} className="px-4 py-2 rounded-full bg-[color:var(--mano-normal)] text-white text-sm font-semibold">Approve</button>
                <button data-testid={`admin-reject-${p.id}`} onClick={() => act(p.id, "reject")} className="px-4 py-2 rounded-full border border-[color:var(--mano-border)] text-sm font-semibold">Reject</button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section>
        <div className="uppercase-label inline-flex items-center gap-1"><AlertOctagon size={14} /> High-severity escalations</div>
        <div className="mt-3 space-y-2">
          {escalations.length === 0 && (
            <div className="rounded-2xl border border-dashed border-[color:var(--mano-border)] p-6 text-center text-[color:var(--mano-ink-soft)]">
              No escalations. Good news.
            </div>
          )}
          {escalations.map((e) => (
            <div key={e.id} className="rounded-2xl border border-[color:var(--mano-border)] bg-white p-5 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold">Report {e.report_id.slice(0, 8)}</div>
                <div className="text-xs text-[color:var(--mano-ink-soft)] inline-flex items-center gap-1"><Clock size={12} /> {new Date(e.created_at).toLocaleString()}</div>
                {e.report && (
                  <div className="text-sm mt-1 capitalize">{e.report.kind} · {(e.report.category || "").replace("_", " ")}</div>
                )}
              </div>
              <SeverityBadge band="high" size="lg" />
            </div>
          ))}
        </div>
      </section>

      <section data-testid="admin-audit-section">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="uppercase-label inline-flex items-center gap-1">
            <ScrollText size={14} /> HIPAA / GDPR audit trail
          </div>
          <input
            data-testid="audit-filter"
            value={auditFilter}
            onChange={(e) => setAuditFilter(e.target.value)}
            placeholder="Filter by action, actor, or resource…"
            className="h-9 rounded-full border border-[color:var(--mano-border)] bg-white px-4 text-sm w-full sm:w-72"
          />
        </div>
        <div className="rounded-2xl border border-[color:var(--mano-border)] bg-white overflow-hidden">
          {audit.length === 0 && (
            <div className="p-6 text-center text-sm text-[color:var(--mano-ink-soft)]">
              No audit events recorded yet.
            </div>
          )}
          {audit.length > 0 && (
            <table className="w-full text-sm" data-testid="audit-table">
              <thead className="bg-[color:var(--mano-bg)] text-[color:var(--mano-ink-soft)]">
                <tr>
                  <th className="text-left px-4 py-2 font-medium uppercase text-[10px] tracking-wider">When (UTC)</th>
                  <th className="text-left px-4 py-2 font-medium uppercase text-[10px] tracking-wider">Actor</th>
                  <th className="text-left px-4 py-2 font-medium uppercase text-[10px] tracking-wider">Action</th>
                  <th className="text-left px-4 py-2 font-medium uppercase text-[10px] tracking-wider">Resource</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[color:var(--mano-border)]">
                {audit
                  .filter((row) => {
                    if (!auditFilter.trim()) return true;
                    const q = auditFilter.trim().toLowerCase();
                    return (
                      (row.action || "").toLowerCase().includes(q) ||
                      (row.actor_email || "").toLowerCase().includes(q) ||
                      (row.resource_type || "").toLowerCase().includes(q) ||
                      (row.resource_id || "").toLowerCase().includes(q)
                    );
                  })
                  .slice(0, 100)
                  .map((row) => (
                    <tr key={row.id} data-testid={`audit-row-${row.id}`}>
                      <td className="px-4 py-2 text-[color:var(--mano-ink-soft)] font-mono text-xs">
                        {(row.at || "").replace("T", " ").slice(0, 19)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="font-medium">{row.actor_email || "—"}</div>
                        <div className="text-[10px] uppercase text-[color:var(--mano-ink-soft)]">{row.actor_role || ""}</div>
                      </td>
                      <td className="px-4 py-2">
                        <span className="inline-flex text-xs font-semibold px-2 py-1 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)]">
                          {row.action}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        <div>{row.resource_type}</div>
                        {row.resource_id && (
                          <div className="text-[color:var(--mano-ink-soft)] font-mono">{row.resource_id.slice(0, 12)}</div>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          )}
        </div>
      </section>
    </div>
  );
}
